import { useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTokensValues } from "../../../hooks/tokens";
import { useLocale } from "../../../hooks/useLocale";
import { useQuestionnaire } from "./useQuestionnaire";
import { useQuestionnaireFormUrl } from "../../../hooks/useQuestionnaireFormUrl";
import { useQuestionRefs } from "../../../hooks/useQuestionRefs/useQuestionRefs";
import { transformAnswers } from "../../../utils";
import { questionnaireFormRefAtom } from "../state/questionnaireFormRefAtom";
import { isSubmittingFormAtom } from "../state/isSubmittingFormAtom";

/**
 * Form submission handling. Validates, transforms answers, and submits.
 *
 * @returns { formRef, handleSubmit, inputValues, tokens, url }
 * @example const { formRef, handleSubmit } = useQuestionnaireSubmit();
 */
export const useQuestionnaireSubmit = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const setFormRef = useSetAtom(questionnaireFormRefAtom);
  const setIsSubmitting = useSetAtom(isSubmittingFormAtom);

  // Store the form ref in the atom so other hooks can access it
  useEffect(() => {
    setFormRef(formRef);
    return () => setFormRef(null);
  }, [setFormRef]);
  const tokens = useTokensValues();
  const { locale } = useLocale();
  const state = useQuestionnaire();
  const {
    answers,
    sections,
    validateForm,
    setErrors,
    setCurrentSectionIndex,
    visibility,
  } = state;
  const url = useQuestionnaireFormUrl();
  const { focusFirstError } = useQuestionRefs();

  const transformedAnswers = useMemo(
    () => transformAnswers(answers, sections, visibility, locale),
    [answers, sections, visibility, locale],
  );

  const validate = useCallback(
    () => validateForm(state),
    [validateForm, state],
  );

  // Generate a hidden input for each answer
  // Array values need one input per value
  // Empty strings are allowed and should be included
  const inputValues = useMemo(
    () =>
      Object.entries(transformedAnswers).reduce<string[][]>(
        (acc, [key, value]) => {
          // Only exclude null and undefined, allow empty strings
          if (value === undefined || value === null) {
            return acc;
          }
          if (Array.isArray(value)) {
            // Empty array must emit a blank entry so Rails clears the field.
            // Without it, the key is absent from the POST and the backend
            // keeps the previously stored value.
            if (value.length === 0) {
              return [...acc, [`${key}[]`, ""]];
            }
            return [...acc, ...value.map(v => [`${key}[]`, v])];
          }
          // Convert to string (this includes empty strings)
          return [...acc, [key, value.toString()]];
        },
        [] as string[][],
      ),
    [transformedAnswers],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const { isValid, errors, firstInvalidSectionIndex } = validate();

      if (isValid) {
        setIsSubmitting(true);

        // Only submit hidden inputs (transformed answers + tokens)
        formRef.current
          ?.querySelectorAll('input:not([type="hidden"])')
          .forEach(el => ((el as HTMLInputElement).disabled = true));

        formRef.current?.submit();
      } else {
        setErrors(errors);
        focusFirstError(errors);
        if (firstInvalidSectionIndex !== undefined) {
          setCurrentSectionIndex(firstInvalidSectionIndex);
        }
      }
    },
    [
      validate,
      setIsSubmitting,
      setErrors,
      focusFirstError,
      setCurrentSectionIndex,
    ],
  );

  return {
    formRef,
    handleSubmit,
    inputValues,
    tokens,
    url,
  };
};
